import { castToBoolean } from '@/utils/config';

type AudioContextCtor = typeof AudioContext;

const CHIME_THROTTLE_MS = 1500;
const CHIME_GAIN = 0.15;
const CHIME_ENABLED =
    import.meta.env.VITE_ALERT_CHIME_ENABLED === undefined
        ? true
        : castToBoolean(import.meta.env.VITE_ALERT_CHIME_ENABLED);

let audioContext: AudioContext | null = null;
let lastPlayedAt = 0;

function resolveAudioContextCtor(): AudioContextCtor | null {
    if (typeof window === 'undefined') return null;
    return (
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: AudioContextCtor })
            .webkitAudioContext ??
        null
    );
}

function getAudioContext(): AudioContext | null {
    if (audioContext?.state === 'closed') audioContext = null;
    if (audioContext) return audioContext;
    const Ctor = resolveAudioContextCtor();
    if (!Ctor) return null;
    audioContext = new Ctor();
    return audioContext;
}

function scheduleTone(
    context: AudioContext,
    frequency: number,
    startTime: number,
    duration: number,
): void {
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'triangle';
    oscillator.frequency.setValueAtTime(frequency, startTime);

    gain.gain.setValueAtTime(0.0001, startTime);
    gain.gain.exponentialRampToValueAtTime(CHIME_GAIN, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    oscillator.connect(gain);
    gain.connect(context.destination);

    oscillator.start(startTime);
    oscillator.stop(startTime + duration);
}

function playChime(context: AudioContext): void {
    const start = context.currentTime;
    scheduleTone(context, 587.33, start, 0.2);
    scheduleTone(context, 440, start + 0.14, 0.28);
}

export function playAlertChime(): void {
    if (!CHIME_ENABLED) return;

    const now = Date.now();
    if (now - lastPlayedAt < CHIME_THROTTLE_MS) return;

    const context = getAudioContext();
    if (!context) return;

    lastPlayedAt = now;

    if (context.state === 'suspended') {
        context
            .resume()
            .then(() => playChime(context))
            .catch(() => {});
        return;
    }

    playChime(context);
}

if (typeof window !== 'undefined') {
    const unlock = () => {
        const context = getAudioContext();
        if (context?.state === 'suspended') {
            context.resume().catch(() => {});
        }
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
    };

    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
}
